import React, { useState, useEffect } from 'react';
import { Typography, Tabs, TabList, Tab, TabPanel, Switch, Select, Option, Chip } from '@mui/joy';
import { Card, Button, Alert } from '@components/ui';
import { notificationService, Notification, NotificationPreferences, NotificationRule } from '@services/notification.service';
import {
  BellIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  Cog6ToothIcon,
  TrashIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

export const Notifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
    fetchRules();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationService.getNotifications({ limit: 50 });
      setNotifications(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notifications');
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await notificationService.getPreferences();
      setPreferences(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch preferences');
    }
  };

  const fetchRules = async () => {
    try {
      const data = await notificationService.getRules();
      setRules(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch rules');
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, isRead: true, readAt: new Date() } : n)
      );
    } catch (err: any) {
      setError(err.message || 'Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true, readAt: new Date() }))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to mark all notifications as read');
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await notificationService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete notification');
    }
  };

  const updatePreferences = async (updates: Partial<NotificationPreferences>) => {
    if (!preferences) return;
    
    try {
      const updated = await notificationService.updatePreferences(updates);
      setPreferences(updated.data);
    } catch (err: any) {
      setError(err.message || 'Failed to update preferences');
    }
  };

  const toggleRule = async (ruleId: string, enabled: boolean) => {
    try {
      await notificationService.toggleRule(ruleId, enabled);
      setRules(prev => 
        prev.map(rule => rule.id === ruleId ? { ...rule, enabled } : rule)
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update rule');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return '🚨';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      default:
        return 'ℹ️';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'critical':
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      default:
        return 'primary';
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Typography level="h2" className="font-bold text-gray-900 dark:text-white">
            Notifications
          </Typography>
          <Typography level="body-md" className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your security alerts and notification preferences
          </Typography>
        </div>
        {unreadCount > 0 && (
          <Button onClick={markAllAsRead} variant="soft" size="sm">
            Mark All Read ({unreadCount})
          </Button>
        )}
      </div>

      {error && (
        <Alert color="danger" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
        <TabList>
          <Tab>
            <BellIcon className="w-4 h-4 mr-2" />
            Notifications
            {unreadCount > 0 && (
              <Chip size="sm" color="danger" variant="solid" className="ml-2">
                {unreadCount}
              </Chip>
            )}
          </Tab>
          <Tab>
            <Cog6ToothIcon className="w-4 h-4 mr-2" />
            Preferences
          </Tab>
          <Tab>
            <EnvelopeIcon className="w-4 h-4 mr-2" />
            Rules
          </Tab>
        </TabList>

        {/* Notifications Tab */}
        <TabPanel value={0} className="pt-6">
          <div className="space-y-4">
            {notifications.length === 0 ? (
              <Card className="p-8 text-center">
                <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <Typography level="body-md" className="text-gray-500">
                  No notifications yet
                </Typography>
              </Card>
            ) : (
              notifications.map(notification => (
                <Card
                  key={notification.id}
                  className={`p-4 ${!notification.isRead ? 'border-l-4 border-l-primary-500' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="text-2xl">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Typography 
                            level="body-md" 
                            className={`font-medium ${!notification.isRead ? 'font-semibold' : ''}`}
                          >
                            {notification.title}
                          </Typography>
                          <Chip 
                            size="sm" 
                            color={getTypeColor(notification.type) as any} 
                            variant="soft"
                          >
                            {notification.type}
                          </Chip>
                          <Chip 
                            size="sm" 
                            color="neutral" 
                            variant="soft"
                          >
                            {notification.category}
                          </Chip>
                        </div>
                        <Typography level="body-sm" className="text-gray-600 dark:text-gray-400 mb-2">
                          {notification.message}
                        </Typography>
                        <Typography level="body-xs" className="text-gray-500">
                          {new Date(notification.createdAt).toLocaleString()}
                          {notification.readAt && (
                            <span className="ml-2">• Read {new Date(notification.readAt).toLocaleString()}</span>
                          )}
                        </Typography>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!notification.isRead && (
                        <Button
                          size="sm"
                          variant="soft"
                          onClick={() => markAsRead(notification.id)}
                          startDecorator={<CheckIcon className="w-3 h-3" />}
                        >
                          Mark Read
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => deleteNotification(notification.id)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabPanel>

        {/* Preferences Tab */}
        <TabPanel value={1} className="pt-6">
          {preferences && (
            <div className="space-y-6">
              <Card className="p-6">
                <Typography level="h4" className="font-semibold mb-4">
                  Delivery Methods
                </Typography>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BellIcon className="w-5 h-5 text-gray-500" />
                      <div>
                        <Typography level="body-md" className="font-medium">
                          In-App Notifications
                        </Typography>
                        <Typography level="body-sm" className="text-gray-500">
                          Show notifications within the application
                        </Typography>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.inApp}
                      onChange={(e) => updatePreferences({ inApp: e.target.checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <EnvelopeIcon className="w-5 h-5 text-gray-500" />
                      <div>
                        <Typography level="body-md" className="font-medium">
                          Email Notifications
                        </Typography>
                        <Typography level="body-sm" className="text-gray-500">
                          Send notifications to your email address
                        </Typography>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.email}
                      onChange={(e) => updatePreferences({ email: e.target.checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <DevicePhoneMobileIcon className="w-5 h-5 text-gray-500" />
                      <div>
                        <Typography level="body-md" className="font-medium">
                          SMS Notifications
                        </Typography>
                        <Typography level="body-sm" className="text-gray-500">
                          Send critical alerts via SMS
                        </Typography>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.sms}
                      onChange={(e) => updatePreferences({ sms: e.target.checked })}
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <Typography level="h4" className="font-semibold mb-4">
                  Categories
                </Typography>
                <div className="space-y-4">
                  {Object.entries(preferences.categories).map(([category, enabled]) => (
                    <div key={category} className="flex items-center justify-between">
                      <Typography level="body-md" className="font-medium capitalize">
                        {category.replace(/([A-Z])/g, ' $1')}
                      </Typography>
                      <Switch
                        checked={enabled}
                        onChange={(e) => updatePreferences({ 
                          categories: { 
                            ...preferences.categories, 
                            [category]: e.target.checked 
                          }
                        })}
                      />
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <Typography level="h4" className="font-semibold mb-4">
                  Severity Threshold
                </Typography>
                <Select
                  value={preferences.severityThreshold}
                  onChange={(_, value) => updatePreferences({ severityThreshold: value as any })}
                >
                  <Option value="all">All notifications</Option>
                  <Option value="medium">Medium and above</Option>
                  <Option value="high">High and above</Option>
                  <Option value="critical">Critical only</Option>
                </Select>
              </Card>
            </div>
          )}
        </TabPanel>

        {/* Rules Tab */}
        <TabPanel value={2} className="pt-6">
          <div className="space-y-4">
            {rules.map(rule => (
              <Card key={rule.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Typography level="body-md" className="font-medium">
                        {rule.name}
                      </Typography>
                      <Chip 
                        size="sm" 
                        color={rule.enabled ? 'success' : 'neutral'} 
                        variant="soft"
                      >
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </Chip>
                    </div>
                    <Typography level="body-sm" className="text-gray-600 dark:text-gray-400 mb-2">
                      {rule.description}
                    </Typography>
                    <Typography level="body-xs" className="text-gray-500">
                      Condition: {rule.condition.type}
                      {rule.condition.severity && ` (${rule.condition.severity} severity)`}
                    </Typography>
                  </div>
                  <Switch
                    checked={rule.enabled}
                    onChange={(e) => toggleRule(rule.id, e.target.checked)}
                  />
                </div>
              </Card>
            ))}
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
};